// SMSTemplateList.js - Lista och hantera SMS-mallar
import React, { useEffect, useState, useContext } from 'react';
import apiClient from '../../apiClient';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../../UserContext';
import { isClinicLevelAdmin } from '../../utils/permissions';
import { toast } from 'react-toastify';
import './SMSTemplateList.css';

function SMSTemplateList() {
  const [templates, setTemplates] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [selectedClinicId, setSelectedClinicId] = useState('');
  const [error, setError] = useState('');
  const [smsStatus, setSmsStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  // Hämta clinics + mallar
  useEffect(() => {
    apiClient.get('/api/clinics')
      .then(res => setClinics(res.data))
      .catch(err => console.error('Fel vid hämtning av clinics:', err));

    apiClient.get('/api/sms/templates')
      .then(res => setTemplates(res.data))
      .catch(err => {
        console.error('Fel vid hämtning av SMS-mallar:', err);
        setError('Misslyckades att hämta mallar.');
      });
  }, []);

  // CRUD-handlers
  const handleEdit = (id) => navigate(`/sms-mallar/edit/${id}`);
  const handleCreate = () => navigate('/sms-mallar/create');

  // Diagnostik: kontrollera SMS-tjänstens config + 46elks-saldo (Spår C)
  const handleCheckStatus = () => {
    setStatusLoading(true);
    setSmsStatus(null);
    apiClient.get('/api/sms/status')
      .then(res => setSmsStatus(res.data))
      .catch(err => {
        console.error('Fel vid SMS-statuskoll:', err);
        toast.error(err.response?.data?.msg || 'Kunde inte hämta SMS-status');
      })
      .finally(() => setStatusLoading(false));
  };

  const handleDelete = (id) => {
    if (!window.confirm('Är du säker på att du vill ta bort denna SMS-mall?')) return;

    apiClient.delete(`/api/sms/templates/${id}`)
      .then(() => {
        setTemplates(templates.filter(t => t.template_id !== id));
        toast.success('SMS-mall borttagen');
      })
      .catch(err => {
        console.error('Fel vid borttagning av mall:', err);
        toast.error(err.response?.data?.msg || 'Misslyckades att ta bort mall');
      });
  };

  // Filtrering
  const filteredTemplates = selectedClinicId
    ? templates.filter(t => t.clinic_id === parseInt(selectedClinicId))
    : templates;

  // Admin-check (global admin + klinikadmin)
  const isAdmin = isClinicLevelAdmin(user);

  // Räkna tecken för förhandsvisning
  const getCharInfo = (body) => {
    const len = body?.length || 0;
    const smsCount = Math.ceil(len / 160) || 1;
    return { len, smsCount };
  };

  return (
    <div className="sms-template-list-container">
      <div className="sms-template-list-header">
        <div className="header-title">
          <button className="back-btn" onClick={() => navigate(-1)}>
            <i className="fas fa-arrow-left"></i>
          </button>
          <h2><i className="fas fa-sms"></i> SMS-mallar</h2>
        </div>
        <div className="header-controls">
          {isAdmin && (
            <select
              value={selectedClinicId}
              onChange={(e) => setSelectedClinicId(e.target.value)}
              className="clinic-filter-select"
            >
              <option value="">Alla kliniker</option>
              {clinics.map(c => (
                <option key={c.clinic_id} value={c.clinic_id}>
                  {c.clinic_name}
                </option>
              ))}
            </select>
          )}
          {isAdmin && (
            <button
              onClick={handleCheckStatus}
              className="sms-status-btn"
              disabled={statusLoading}
              title="Visa 46elks-config och saldo"
            >
              <i className="fas fa-heartbeat"></i>
              {statusLoading ? ' Kontrollerar…' : ' SMS-status'}
            </button>
          )}
          {isAdmin && (
            <button onClick={handleCreate} className="create-button">
              <i className="fas fa-plus"></i> Skapa ny mall
            </button>
          )}
        </div>
      </div>

      {smsStatus && (
        <div className="sms-status-panel">
          <div className="sms-status-panel-header">
            <h3><i className="fas fa-heartbeat"></i> SMS-tjänst (46elks)</h3>
            <button
              className="sms-status-close"
              onClick={() => setSmsStatus(null)}
              title="Stäng"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>

          <div className="sms-status-grid">
            <div className={`sms-status-item ${smsStatus.configured ? 'ok' : 'bad'}`}>
              <span className="sms-status-label">Konfiguration</span>
              <span className="sms-status-value">
                <i className={`fas ${smsStatus.configured ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
                {smsStatus.configured ? 'Nycklar inlästa' : 'Nycklar saknas'}
              </span>
              {!smsStatus.configured && (
                <span className="sms-status-hint">
                  {!smsStatus.username_set && 'ELKS_API_USERNAME saknas. '}
                  {!smsStatus.password_set && 'ELKS_API_PASSWORD saknas.'}
                </span>
              )}
            </div>

            <div className={`sms-status-item ${smsStatus.sender_length_ok ? 'ok' : 'warn'}`}>
              <span className="sms-status-label">Avsändare</span>
              <span className="sms-status-value">
                {smsStatus.clinic_sender || smsStatus.sender || '—'}
              </span>
              {smsStatus.clinic_sender && (
                <span className="sms-status-hint">Klinik-override (env-default: {smsStatus.sender || '—'}).</span>
              )}
              {!smsStatus.sender_length_ok && (
                <span className="sms-status-hint">Längre än 11 tecken — kapas vid utskick.</span>
              )}
            </div>

            <div className={`sms-status-item ${smsStatus.account ? 'ok' : (smsStatus.configured ? 'bad' : 'neutral')}`}>
              <span className="sms-status-label">Saldo</span>
              <span className="sms-status-value">
                {smsStatus.account
                  ? (smsStatus.account.balance_display
                      ?? (smsStatus.account.balance_raw != null ? String(smsStatus.account.balance_raw) : '—'))
                  : (smsStatus.account_error || 'Ej tillgängligt')}
              </span>
              {smsStatus.account?.displayname && (
                <span className="sms-status-hint">Konto: {smsStatus.account.displayname}</span>
              )}
            </div>
          </div>

          <p className="sms-status-note">
            <i className="fas fa-info-circle"></i> Att avsändar-ID är godkänt hos 46elks
            går bara att bekräfta med ett skarpt test-SMS — det syns inte här.
          </p>
        </div>
      )}

      {error && <p className="error-message">{error}</p>}

      <div className="template-list">
        {filteredTemplates.length === 0 ? (
          <div className="no-templates">
            <i className="fas fa-inbox"></i>
            <p>Inga SMS-mallar hittades.</p>
            {isAdmin && (
              <button onClick={handleCreate} className="create-first-btn">
                Skapa din första mall
              </button>
            )}
          </div>
        ) : (
          filteredTemplates.map(template => {
            const { len, smsCount } = getCharInfo(template.body);
            return (
              <div key={template.template_id} className="template-card">
                <div className="template-info">
                  <h3 className="template-name">{template.template_name}</h3>
                  <div className="template-meta">
                    <span className="char-count">
                      <i className="fas fa-font"></i> {len} tecken
                    </span>
                    <span className="sms-count">
                      <i className="fas fa-sms"></i> {smsCount} SMS
                    </span>
                    {template.clinic_id && (
                      <span className="clinic-badge">
                        {clinics.find(c => c.clinic_id === template.clinic_id)?.clinic_name || 'Klinik'}
                      </span>
                    )}
                    {!template.clinic_id && (
                      <span className="global-badge">Global</span>
                    )}
                  </div>
                  <p className="template-preview">
                    {template.body?.substring(0, 120)}{template.body?.length > 120 ? '...' : ''}
                  </p>
                </div>

                <div className="template-actions">
                  {isAdmin && (
                    <>
                      <button
                        className="edit-button"
                        onClick={() => handleEdit(template.template_id)}
                        title="Redigera"
                      >
                        <i className="fas fa-edit"></i> Redigera
                      </button>
                      <button
                        className="delete-button"
                        onClick={() => handleDelete(template.template_id)}
                        title="Ta bort"
                      >
                        <i className="fas fa-trash"></i> Ta bort
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default SMSTemplateList;
